/**
 * Responsibility: the cards taken off the discovery board to keep an eye on.
 *
 * Its own page for the same reason likes are: watching a card is not owning
 * one. It used to be four tabs on the discovery board - modern watching,
 * vintage watching, and a bin for each - which made the board about
 * bookkeeping as much as discovery.
 *
 * Both eras are shown together. The era is a property of the card, not a
 * place it lives, and having to remember which tab a card was set aside on
 * is a worse question than the page was built to answer.
 *
 * The figure that matters is the move: a watched card stores what it was
 * worth at the moment it was set aside, and the only reason to keep the list
 * is to see which of them has gone up since.
 *
 * **Two sources, one page.** A card reaches this list either by being taken
 * off the discovery board or by being watched from search - and where it came
 * from is not what somebody is here to think about. The grid merges them,
 * because a shelf of cards is a shelf of cards. The tables stay apart, because
 * the two carry genuinely different columns: a discovery row has a PSA 10
 * population behind it and a searched card has a whole price ladder.
 */

import { useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { MetricCard, PageContainer } from '@kit/components';
import { ToolbarIconButton } from '@kit/components/buttons';
import { ArchiveTable } from '../components/grading/ArchiveTable';
import { MarketTable } from '../components/market/MarketTable';
import { MarketCardDialog } from '../components/market/MarketCardDialog';
import {
  useMarketCard,
  useSaved,
  useToggleWatch,
  useWatchedCards,
} from '../api/hooks/useMarket';
import { ShelfGrid } from '../components/shelf/ShelfGrid';
import { ShelfToolbar } from '../components/shelf/ShelfToolbar';
import type { ShelfView } from '../components/shelf/ShelfToolbar';
import { fromMarketCard, fromWatched } from '../components/shelf/shelfCard';
import { useDiscoveryArchive, useRestoreCard } from '../api/hooks/useDiscovery';
import { toNumber } from '../components/grading/money';
import { FittedTable } from '../components/shelf/FittedTable';
import { useFillHeight } from '../hooks/useFillHeight';
import { usePageState } from '../hooks/usePageState';

export function WatchingPage() {
  const [view, setView] = usePageState<ShelfView>('watching.view', 'table', 'durable');
  const [expanded, setExpanded] = usePageState('watching.expanded', false, 'durable');

  const theme = useTheme();
  const onDesktop = useMediaQuery(theme.breakpoints.up('md'));
  // Measured, not guessed: the room under the table changes when the
  // figures above it are hidden, and a constant allowance cannot know.
  const [fillRef, tableHeight] = useFillHeight();

  // Both eras. The key does not say which one a card came from, and
  // neither should the operator have to.
  const modern = useDiscoveryArchive('MODERN', 'WATCHING');
  const vintage = useDiscoveryArchive('VINTAGE', 'WATCHING');
  const restoreModern = useRestoreCard('MODERN');
  const restoreVintage = useRestoreCard('VINTAGE');

  const rows = useMemo(
    () => [...(modern.data?.rows ?? []), ...(vintage.data?.rows ?? [])],
    [modern.data, vintage.data],
  );
  const [openKey, setOpenKey] = usePageState<string | null>('watching.open', null);
  const openCard = useMarketCard(openKey);
  const saved = useSaved();
  const watchedCards = useMemo(() => saved.data?.watching ?? [], [saved.data]);
  const watchedKeys = useMemo(
    () => new Set(watchedCards.map((row) => row.product_key)),
    [watchedCards],
  );
  const toggleWatch = useToggleWatch(watchedKeys);
  const market = useWatchedCards();

  const cards = useMemo(
    () => [...(market.data?.results ?? []).map(fromMarketCard), ...rows.map(fromWatched)],
    [market.data, rows],
  );
  const modernKeys = useMemo(
    () => new Set((modern.data?.rows ?? []).map((row) => row.discovery_key)),
    [modern.data],
  );

  /**
   * Which grid cards came from the market rather than the discovery board.
   *
   * **Two different things are watched here and they are undone differently.**
   * A market card is un-watched; a discovery row is put back on the board it
   * came off. The grid merges both into `ShelfCard`, which carries no hint of
   * which is which, and its `key` is a `product_key` for one and a
   * `discovery_key` for the other - different namespaces entirely. Calling
   * restore on a market card therefore looked up a key that could not match,
   * fell through to the vintage board, and quietly did nothing.
   */
  const marketKeys = useMemo(
    () => new Set((market.data?.results ?? []).map((row) => row.key)),
    [market.data],
  );

  /** Put a card back on the board it came off. */
  const restore = (discoveryKey: string) => {
    if (modernKeys.has(discoveryKey)) restoreModern.mutate(discoveryKey);
    else restoreVintage.mutate(discoveryKey);
  };

  const risen = rows.filter((row) => (toNumber(row.change_percent) ?? 0) > 0).length;
  const fallen = rows.filter((row) => (toNumber(row.change_percent) ?? 0) < 0).length;
  const unpriced = rows.filter((row) => row.latest_psa10 === null).length;

  const busy = restoreModern.isPending || restoreVintage.isPending || toggleWatch.isPending;
  const error = modern.error ?? vintage.error ?? restoreModern.error ?? restoreVintage.error;
  const loading = modern.isLoading || vintage.isLoading;

  const empty =
    'Nothing being watched. The eye on a discovery row takes a card off the board and keeps it here.';

  return (
    <PageContainer disableBodyScroll={onDesktop}>
      <Stack spacing={1.5} sx={{ minWidth: 0, height: '100%' }}>
        {error ? <Alert severity="error">{(error as Error).message}</Alert> : null}

        {!expanded ? (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
              flexShrink: 0,
            }}
          >
            <MetricCard
              label="Watching"
              value={String(rows.length + watchedCards.length)}
              helper="from search and the board"
            />
            <MetricCard label="Up" value={String(risen)} helper="since it was set aside" />
            <MetricCard label="Down" value={String(fallen)} helper="since it was set aside" />
            <MetricCard
              label="No reading"
              value={String(unpriced)}
              helper="never re-priced yet"
            />
          </Box>
        ) : null}

        <ShelfToolbar
          view={view}
          onView={setView}
          expanded={expanded}
          onExpanded={setExpanded}
        />

        {view === 'grid' ? (
          <ShelfGrid
            cards={cards}
            emptyMessage={loading ? 'Loading the watch list' : empty}
            corner={(card) => {
              const fromMarket = marketKeys.has(card.key);
              return (
                <ToolbarIconButton
                  aria-label={
                    fromMarket
                      ? 'Stop watching this card'
                      : 'Put this card back on the discovery board'
                  }
                  disabled={busy}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (fromMarket) toggleWatch.mutate(card.key);
                    else restore(card.key);
                  }}
                  sx={{ bgcolor: 'rgba(0,0,0,0.6)', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' } }}
                >
                  {fromMarket ? (
                    <VisibilityOffIcon fontSize="small" sx={{ color: 'common.white' }} />
                  ) : (
                    <UndoIcon fontSize="small" sx={{ color: 'common.white' }} />
                  )}
                </ToolbarIconButton>
              );
            }}
          />
        ) : (
          <FittedTable fillRef={fillRef}>
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              {(market.data?.results ?? []).length > 0 ? (
                <MarketTable
                  rows={market.data?.results ?? []}
                  loading={market.isLoading}
                  busy={toggleWatch.isPending}
                  onOpen={(row) => setOpenKey(row.key)}
                  onAdd={() => undefined}
                  onLike={() => undefined}
                  liked={new Set()}
                  watched={watchedKeys}
                  onWatch={(row) => toggleWatch.mutate(row.key)}
                  maxHeight={onDesktop ? tableHeight : undefined}
                />
              ) : null}
              {rows.length > 0 ? (
                <ArchiveTable
                  rows={rows}
                  loading={loading}
                  busy={busy}
                  onRestore={(row) => restore(row.discovery_key)}
                />
              ) : null}
            </Stack>
          </FittedTable>
        )}
      </Stack>

      <MarketCardDialog
        card={openCard.data?.result ?? null}
        pending={openKey !== null}
        onClose={() => setOpenKey(null)}
        onAdd={() => setOpenKey(null)}
      />
    </PageContainer>
  );
}
